<template>
  <b-card-code
    no-body
  >
    <b-row
      class="px-2 mb-2"
    >
      <b-col
        v-if="$can('create', 'subnet-categories')"
        order-md="2"
        order="1"
        class="text-right d-flex align-items-end justify-content-end"
      >
        <div class="d-flex justify-content-end align-items-center mr-2">
          <div
            v-if="(searchFields && searchFields.length > 0) || (orderBy && orderBy.length > 0) || (sortedBy && sortedBy.length > 0)"
            class="d-flex justify-content-end align-items-center mr-2"
          >
            <b-button
              variant="primary"
              class="reset-filters-button"
              @click="resetFilter"
            >
              <feather-icon
                icon="XIcon"
                size="12"
                stroke="#f0554e"
                class="text-body align-middle mr-25"
              />
              Reset filters
            </b-button>
          </div>
          <b-button
            to="/ipam/categories/create"
            variant="primary"
            class="mr-2"
          >
            Create
          </b-button>
          <b-button
            v-if="$can('additional-action-delete', 'subnet-categories')"
            variant="primary"
            class="mr-2"
            @click="toggleFilterOnlyTrashed"
          >
            <span v-if="this.onlyTrashed">Without deleted</span>
            <span v-else>Only deleted</span>
          </b-button>
          <div
            v-if="$can('delete', 'subnet-categories')"
            label-class="label-class"
            class="with-deleted-checkbox d-flex flex-direction-column"
          >
            <span>With deleted</span>
            <b-form-checkbox
              class="mr-0"
              switch
              inline
              @change="setFilterWithTrashed"
            />
          </div>
        </div>
      </b-col>
    </b-row>

    <b-card-body class="no-padding-table">

      <!-- table -->
      <vue-good-table
        ref="VueGoodTable"
        :is-loading="isLoading"
        :columns="fields"
        :rows="items"
        compact-mode
        :total-rows="pagination.total"
        class="category-table"
        :row-style-class="rowStyleClassFn"
        mode="remote"
        :sort-options="{
          enabled: true,
          multipleColumns: true,
          initialSortBy: {field: orderBy, type: sortedBy}
        }"
        :pagination-options="{
          enabled: true,
          perPage: perPage,
          setCurrentPage: page
        }"
        @on-sort-change="onSortChange"
        @on-column-filter="columnFilterFn"
      >
        <template slot="loadingContent">
          <b-spinner
            variant="primary"
            label="Spinning"
          />
        </template>
        <template
          slot="table-row"
          slot-scope="props"
        >
          <span v-if="props.column.field === 'action'">
            <span>
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <b-dropdown-item
                  v-if="$can('update', 'subnet-categories')"
                  @click.prevent="toEdit(props.row.id)"
                >
                  <feather-icon
                    icon="Edit2Icon"
                    class="mr-50"
                  />
                  <span>Edit</span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="!props.row.is_deleted && $can('delete', 'subnet-categories')"
                  @click.prevent="deleteHandler(props.row.id)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    class="mr-50"
                  />
                  <span>Delete</span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="props.row.is_deleted && $can('delete', 'subnet-categories')"
                  @click.prevent="deleteHandler(props.row.id)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    class="mr-50"
                  />
                  <span>Permanently delete</span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="props.row.is_deleted && $can('delete', 'subnet-categories')"
                  @click.prevent="recoverRow(props.row.id)"
                >
                  <feather-icon
                    icon="RefreshCcwIcon"
                    class="mr-50"
                  />
                  <span>Recover</span>
                </b-dropdown-item>
              </b-dropdown>
            </span>
          </span>
        </template>

        <!-- pagination -->
        <template
          slot="pagination-bottom"
        >
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap">
                Showing 1 to
              </span>
              <b-form-select
                v-model="perPage"
                :options="pageSizeOptions"
                class="mx-1"
                @change="perPageChanged"
              />
              <span class="text-nowrap"> of {{ pagination.total }} entries </span>
            </div>
            <div>
              <b-pagination
                v-if="pagination.last_page !== 1"
                :value="page"
                :total-rows="pagination.total"
                :per-page="perPage"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @change="changePage"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
      <b-modal
        id="modalSoftDelete"
        ref="modalSoftDelete"
        :title="deleteErrorPopUpInfo.title"
        centered
        ok-only
        ok-variant="secondary"
        ok-title="Cancel"
      >
        <p v-html="deleteErrorPopUpInfo.content" />
      </b-modal>
    </b-card-body>
  </b-card-code>
</template>

<script>
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import {
  BCardBody,
  BPagination,
  BButton,
  BRow,
  BCol,
  BDropdown,
  BDropdownItem,
  BFormSelect,
  BModal,
  BSpinner,
  VBTooltip,
  BFormCheckbox,
} from 'bootstrap-vue'
import { mapActions, mapState, mapMutations } from 'vuex'
import IndexPagesMixin from '@/mixins/IndexPages.vue'
import { VueGoodTable } from 'vue-good-table'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BFormCheckbox,
    BCardCode,
    BCardBody,
    BButton,
    BPagination,
    BRow,
    BCol,
    BDropdown,
    BDropdownItem,
    BFormSelect,
    BModal,
    BSpinner,
    VueGoodTable,
  },
  mixins: [
    IndexPagesMixin,
  ],
  data() {
    return {
      isLoading: true,
      uppdateRouteName: 'categories-update',
      deleteErrorPopUpInfo: {},

      fields: [
        { field: 'id', label: 'ID' },
        {
          field: 'name',
          label: 'Name',
          filterOptions: {
            enabled: true,
            placeholder: 'Name',
            filterValue: '',
          },
        },
        {
          field: 'action',
          label: 'Action',
          sortable: false,
          thClass: 'action-th-width',
          hidden: !this.$can('update', 'subnet-categories') && !this.$can('delete', 'subnet-categories'),
        },
      ],
    }
  },
  computed: {
    ...mapState('ipamCategories', {
      items: 'items',
      pagination: 'pagination',
      searchFields: 'searchFields',
      orderBy: 'orderBy',
      sortedBy: 'sortedBy',
    }),
  },
  methods: {
    ...mapActions('ipamCategories', {
      fetchItems: 'getItems',
      deleteRecord: 'deleteItem',
      setSearchFields: 'setSearchFields',
      setOrderBy: 'setOrderBy',
      setSortedBy: 'setSortedBy',
      recoverRecord: 'recoverItem',
    }),
    ...mapMutations('ipamCategories', ['RESET_STATE']),
    async deleteHandler(id) {
      const errorRes = await this.deleteRow(id)
      this.deleteErrorPopUpInfo = errorRes
    },
    rowStyleClassFn(row) {
      return row.is_deleted ? 'deleted-class' : ''
    },
  },
}
</script>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';
  .vgt-table{
      th{
          vertical-align: middle !important;
          span{
              font-size: 13px;
          }
      }
  }
  .category-table {
    .deleted-class {
      .vgt-checkbox-col{
        background-color: rgba(255, 0, 0, 0.185) !important;
        input{
          display: none;
        }
      }
      .vgt-left-align {
        background-color: rgba(255, 0, 0, 0.185) !important;
      }
    }
  }
</style>
